import { formatDateTime } from '@/lib/date-time';
/**
 * @file components/audit-tasks/phases/PhaseReport.tsx
 * @description 階段 5：報告編製（撰寫 + 單級審核 MVP）。
 *
 * 內容：
 *  - 報告主表（編號 / 狀態 / 版本數）
 *  - 「AI 生成草稿」按鈕：呼叫 POST /api/cases/:caseId/audit-report/draft
 *  - 版本列表（時間倒序，可展開查看 JSON 內容）
 *  - 單級審核：
 *      submit → audit-report.submit + 本案主導稽核員本人（定稿送審）
 *      approve / reject → 由 capability audit-report.review 啟用（admin / manager）
 *      reject 需在 Modal 中填寫 reviewNotes
 *
 * 後端 API：
 *   GET  /api/cases/:caseId/audit-report
 *   POST /api/cases/:caseId/audit-report/draft
 *   POST /api/cases/:caseId/audit-report/submit   body: { versionId }
 *   POST /api/cases/:caseId/audit-report/approve  body: { versionId, reviewNotes? }
 *   POST /api/cases/:caseId/audit-report/reject   body: { versionId, reviewNotes }
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { FileText, Sparkles, Eye, Send, Check, X, Palette } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userCan, type AuditTaskDetail } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { StatusPill, type StatusPillProps } from '@/components/shell';
import { AuditReportEditor } from '@/components/audit-report/AuditReportEditor';

interface Props {
  task: AuditTaskDetail;
}

/** 報告樣式模板（對應 report_style_templates，序40 風格選擇用） */
interface ReportStyleTemplate {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
}

/** 報告版本記錄（對應 audit_report_versions） */
interface AuditReportVersion {
  id: number;
  version: number;
  content: Record<string, unknown>;
  aiGenerated: boolean;
  styleTemplateId: number | null;
  submittedBy: number | null;
  submittedAt: string | null;
  reviewedBy: number | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAt: string;
}

/** 報告主表（對應 audit_reports，附帶版本列表） */
interface AuditReport {
  id: number;
  caseId: number;
  code: string;
  currentVersionId: number | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  versions: AuditReportVersion[];
}

const STATUS_TONE: Record<string, StatusPillProps['tone']> = {
  草稿: 'muted',
  評審中: 'warning',
  終稿: 'success',
};

export function PhaseReport({ task }: Props) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  /** 是否本案主導稽核員（cases.assignedTeamLeadId）——定稿送審唯一責任人 */
  const isCaseLead = user != null && task.caseAssignedTeamLeadId === user.id;
  const canSubmit = user ? userCan(user, 'audit-report.submit') && isCaseLead : false;
  const canReview = user ? userCan(user, 'audit-report.review') : false;
  /** 是否可管理報告樣式模板（auditor）——決定是否顯示「前往設定風格」鏈接 */
  const canManageStyle = user ? userCan(user, 'report-style.manage') : false;

  /** 彈窗查看內容的版本 ID（null=未開啟；內容從即時 report 資料查找，生成後自動刷新） */
  const [viewingVersionId, setViewingVersionId] = useState<number | null>(null);
  /** 退回 Modal 狀態 */
  const [rejectTarget, setRejectTarget] = useState<AuditReportVersion | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  /** 序44：定稿送審 Modal 狀態（勾選驗證建議 + 簽名） */
  const [finalizeTarget, setFinalizeTarget] = useState<AuditReportVersion | null>(null);
  const [recommendation, setRecommendation] = useState<'issue' | 'partial' | 'no_issue' | null>(
    null,
  );
  const [recommendationNote, setRecommendationNote] = useState('');
  const [leadSignatureId, setLeadSignatureId] = useState<number | null>(null);
  /** 序40：AI 生成時選中的報告風格模板 ID（undefined=用系統預設/使用者預設模板） */
  const [selectedStyleId, setSelectedStyleId] = useState<number | undefined>(undefined);

  // ─── 取得稽核報告 ─────────────────────────────────────────────────
  const { data, isLoading } = useQuery<{ item: AuditReport | null }>({
    queryKey: ['audit-report', task.caseId],
    queryFn: () => apiClient.get(`/api/cases/${task.caseId}/audit-report`),
  });
  const report = data?.item ?? null;
  const latestVersion = report?.versions?.[0] ?? null;
  /** 目前彈窗查看的版本（從即時資料查找，refetch 後自動反映最新 content） */
  const viewingVersion = report?.versions.find((v) => v.id === viewingVersionId) ?? null;
  /** 當前等待評審的版本（status='評審中' 且 currentVersionId 指向） */
  const pendingReviewVersion =
    report?.status === '評審中' && report.currentVersionId
      ? (report.versions.find((v) => v.id === report.currentVersionId) ?? null)
      : null;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['audit-report', task.caseId] });

  // ─── 序40：取得當前用戶的報告風格模板清單 ────────────────────────────
  // 僅可生成草稿者需要（拉自己的 /api/me/report-styles；admin/manager 通常為空清單）。
  // 無 report-style.manage 能力者後端會 403，故不啟用查詢，避免無謂錯誤。
  const { data: stylesData } = useQuery<{ items: ReportStyleTemplate[] }>({
    queryKey: ['report-styles', 'mine'],
    queryFn: () => apiClient.get('/api/me/report-styles'),
    enabled: canManageStyle,
  });
  const styleTemplates = stylesData?.items ?? [];

  // ─── 觸發 AI 草稿生成 ─────────────────────────────────────────────
  const draftMutation = useMutation({
    // 同步生成：回應即帶最新報告（含新版本），成功後直接刷新列表。
    // 序40：帶上選中的風格模板 ID（reportStyleTemplateId）；未選則不傳，後端回退預設模板。
    mutationFn: () =>
      apiClient.post(`/api/cases/${task.caseId}/audit-report/draft`, {
        ...(selectedStyleId != null ? { reportStyleTemplateId: selectedStyleId } : {}),
      }),
    onSuccess: () => {
      void message.success('AI 報告草稿已生成');
      void invalidate();
    },
    onError: (e: Error) => void message.error(e.message || 'AI 草稿生成失敗'),
  });

  // ─── 提交評審（序44：定稿附驗證建議 + 主導稽核員簽名；通知小組成員） ─────
  const submitMutation = useMutation({
    mutationFn: (versionId: number) =>
      apiClient.post(`/api/cases/${task.caseId}/audit-report/submit`, {
        versionId,
        recommendation: recommendation ?? undefined,
        recommendationNote: recommendationNote.trim() || undefined,
        leadSignatureFileId: leadSignatureId ?? undefined,
      }),
    onSuccess: () => {
      void message.success('已定稿送審，並通知稽核小組成員');
      setFinalizeTarget(null);
      setRecommendation(null);
      setRecommendationNote('');
      void invalidate();
    },
    onError: (e: Error) => void message.error(e.message || '提交失敗'),
  });

  // 序44：導入本人員工簽名檔作為定稿簽名
  const importSignatureMut = useMutation({
    mutationFn: async () => apiClient.get<{ fileId: number | null }>('/api/auth/me/signature'),
    onSuccess: (res) => {
      if (res.fileId) {
        setLeadSignatureId(res.fileId);
        void message.success('已導入我的簽名檔');
      } else {
        void message.warning('尚未設定簽名檔，請至員工檔案上傳簽名');
      }
    },
    onError: (e: Error) => {
      console.error('[PhaseReport] import signature failed', e);
      void message.error(e.message || '導入簽名檔失敗');
    },
  });

  /** 序84：報告是否曾被初審退回（任一版本有退回備註）——按鈕文案改「重新上報」 */
  const wasReturned = (report?.versions ?? []).some((v) => v.reviewNotes);

  /** 序85：上報文件完整度（表單完成日期 + 應附文件覆蓋；不完整警示驗證組補充） */
  const checklistQuery = useQuery<{
    item: {
      forms: {
        templateCode: string | null;
        templateName: string | null;
        completedAt: string | null;
      }[];
      categories: { key: string; uploaded: boolean }[];
      stats: {
        formsDone: number;
        formsTotal: number;
        docsDone: number;
        docsTotal: number;
        complete: boolean;
      };
    };
  }>({
    queryKey: ['dossier-checklist', task.caseId],
    queryFn: () => apiClient.get(`/api/cases/${task.caseId}/dossier-checklist`),
  });
  const checklist = checklistQuery.data?.item ?? null;

  // ─── 核准 ─────────────────────────────────────────────────────────
  const approveMutation = useMutation({
    mutationFn: (versionId: number) =>
      apiClient.post(`/api/cases/${task.caseId}/audit-report/approve`, { versionId }),
    onSuccess: () => {
      void message.success('報告已核准（終稿）');
      void invalidate();
    },
    onError: (e: Error) => void message.error(e.message || '核准失敗'),
  });

  // ─── 退回 ─────────────────────────────────────────────────────────
  const rejectMutation = useMutation({
    mutationFn: (args: { versionId: number; reviewNotes: string }) =>
      apiClient.post(`/api/cases/${task.caseId}/audit-report/reject`, args),
    onSuccess: () => {
      void message.success('報告已退回');
      setRejectTarget(null);
      setRejectNotes('');
      void invalidate();
    },
    onError: (e: Error) => void message.error(e.message || '退回失敗'),
  });

  // ─── 渲染 ─────────────────────────────────────────────────────────
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 序85：上報文件完整度（ZHSR05-1/ZHSR05-5 口徑）——不完整警示驗證組補充 */}
      {checklist && (
        <Alert
          type={checklist.stats.complete ? 'success' : 'warning'}
          showIcon
          message={
            checklist.stats.complete
              ? `上報文件完整度：表單 ${checklist.stats.formsDone}/${checklist.stats.formsTotal} 已完成、應附文件 ${checklist.stats.docsDone}/${checklist.stats.docsTotal} 類已上傳`
              : `上報文件不完整：表單 ${checklist.stats.formsDone}/${checklist.stats.formsTotal} 已完成、應附文件 ${checklist.stats.docsDone}/${checklist.stats.docsTotal} 類已上傳`
          }
          description={
            !checklist.stats.complete && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                未完成表單：
                {checklist.forms
                  .filter((f) => !f.completedAt)
                  .map((f) => f.templateCode ?? f.templateName ?? '')
                  .filter(Boolean)
                  .join('、') || '（無）'}
                ；未上傳分類：
                {checklist.categories
                  .filter((cat) => !cat.uploaded)
                  .map((cat) => cat.key)
                  .join('、') || '（無）'}
                。稽核報告＝全部必須上報的稽核文檔（含表單完成日期），請於定稿上報前補齊。
              </Typography.Text>
            )
          }
        />
      )}

      {/* 報告主表卡片 */}
      <Card
        size="small"
        title={
          <Space size={6}>
            <FileText size={14} />
            <span>稽核報告</span>
            {report && (
              <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                {report.code}
              </Typography.Text>
            )}
          </Space>
        }
        extra={
          <Space size={8}>
            {/* 序40：報告風格選擇——僅可管理風格模板者（auditor）顯示。
                有模板時可選一個風格；無模板時提供「前往設定風格」鏈接。 */}
            {canManageStyle &&
              report?.status !== '終稿' &&
              (styleTemplates.length > 0 ? (
                <Tooltip title="選擇一個報告撰寫風格模板，AI 生成時將依此語氣行文；不選則用您的預設模板">
                  <Select<number>
                    size="small"
                    style={{ minWidth: 160 }}
                    allowClear
                    placeholder="報告風格（預設）"
                    value={selectedStyleId}
                    onChange={(v) => setSelectedStyleId(v)}
                    suffixIcon={<Palette size={13} />}
                    options={styleTemplates.map((s) => ({
                      value: s.id,
                      label: s.isDefault ? `${s.name}（預設）` : s.name,
                    }))}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="尚未建立任何報告風格模板，AI 將使用系統標準公文語氣">
                  <Link to="/me/report-styles">
                    <Button size="small" icon={<Palette size={13} />}>
                      設定報告風格
                    </Button>
                  </Link>
                </Tooltip>
              ))}
            <Tooltip title="由系統自動產生稽核報告初稿，生成約需數秒，完成後直接顯示於下方版本列表">
              <Button
                size="small"
                type="primary"
                icon={<Sparkles size={14} />}
                loading={draftMutation.isPending}
                disabled={report?.status === '終稿'}
                onClick={() => draftMutation.mutate()}
              >
                {report ? '重新生成 AI 草稿' : 'AI 生成草稿'}
              </Button>
            </Tooltip>
          </Space>
        }
      >
        {isLoading && <Typography.Text type="secondary">載入中…</Typography.Text>}

        {!isLoading && !report && (
          <Alert
            type="info"
            showIcon
            message="尚未建立稽核報告"
            description={
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                完成實地稽核並簽退後，點擊右上「AI
                生成草稿」，系統會自動為您整理本次稽核報告的初稿。
                生成約需數秒，完成後直接顯示在下方的版本列表。
              </Typography.Text>
            }
          />
        )}

        {!isLoading && report && (
          <Descriptions column={3} size="small">
            <Descriptions.Item label="報告編號">
              <span style={{ fontFamily: 'monospace' }}>{report.code}</span>
            </Descriptions.Item>
            <Descriptions.Item label="狀態">
              <StatusPill tone={STATUS_TONE[report.status] ?? 'muted'} size="sm">
                {report.status}
              </StatusPill>
            </Descriptions.Item>
            <Descriptions.Item label="版本數">{report.versions.length}</Descriptions.Item>
            <Descriptions.Item label="建立時間">
              {formatDateTime(report.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="最後更新">
              {formatDateTime(report.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="當前版本">
              {latestVersion ? `v${latestVersion.version}` : '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* 序17：報告流程「下一步」導引——依報告狀態與當前使用者角色給明確文案，
          消除「提交評審後在哪評審 / 組長生成後在哪確認」的困惑。 */}
      {report && report.status === '草稿' && (
        <Alert
          type="info"
          showIcon
          message="下一步：由本案主導稽核員定稿送審"
          description={
            <Typography.Text style={{ fontSize: 13 }}>
              報告目前為<b>草稿</b>。校對／編輯完成後，請
              {isCaseLead ? (
                <>
                  由您（主導稽核員）在下方版本列表點擊「<b>提交評審</b>」送交管理組核准。
                </>
              ) : (
                <>
                  由<b>本案主導稽核員</b>在下方版本列表點擊「提交評審」送交管理組核准（您可協助 AI
                  校對與編輯，但送審須由主導稽核員操作）。
                </>
              )}
            </Typography.Text>
          }
        />
      )}
      {report && report.status === '評審中' && !canReview && (
        <Alert
          type="warning"
          showIcon
          message="已提交評審，等待管理組核准"
          description={
            <Typography.Text style={{ fontSize: 13 }}>
              報告已進入<b>評審中</b>，正等待<b>管理員／管理組</b>核准或退回。
              核准後案件自動移交驗證決定流程；若被退回，報告會回到草稿狀態並附退回理由，屆時請依理由修改後重新送審。
            </Typography.Text>
          }
        />
      )}
      {report && report.status === '終稿' && (
        <Alert
          type="success"
          showIcon
          message="報告已核准（終稿），案件已移交驗證決定"
          description={
            <Typography.Text style={{ fontSize: 13 }}>
              報告已定稿並鎖定，案件已進入<b>驗證決定</b>
              流程，可於「驗證決定管理」中查看後續審議與發證進度。
            </Typography.Text>
          }
        />
      )}

      {/* 評審中提示 + 核准/退回入口（管理員、經理可見） */}
      {report && pendingReviewVersion && canReview && (
        <Card size="small" title="等待評審">
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text>
              版本 <Tag color="blue">v{pendingReviewVersion.version}</Tag> 已由稽核員提交，請決定：
            </Typography.Text>
            <Space>
              <Button
                type="primary"
                icon={<Check size={14} />}
                loading={approveMutation.isPending}
                onClick={() => approveMutation.mutate(pendingReviewVersion.id)}
              >
                核准（標記終稿）
              </Button>
              <Button
                danger
                icon={<X size={14} />}
                onClick={() => {
                  setRejectTarget(pendingReviewVersion);
                  setRejectNotes('');
                }}
              >
                退回修改
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      {/* 版本列表 */}
      {report && (
        <Card
          size="small"
          title={
            <Space size={6}>
              <span>版本記錄</span>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                共 {report.versions.length} 版
              </Typography.Text>
            </Space>
          }
        >
          {report.versions.length === 0 ? (
            <Empty
              description="暫無版本，點擊上方「AI 生成草稿」開始"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {report.versions.map((v) => {
                const isLatest = v.id === latestVersion?.id;
                const isPendingReview = v.id === pendingReviewVersion?.id;
                const canSubmitThisVersion =
                  canSubmit &&
                  !v.submittedAt &&
                  report.status !== '終稿' &&
                  report.status !== '評審中';

                return (
                  <Card
                    key={v.id}
                    size="small"
                    type="inner"
                    title={
                      <Space size={8}>
                        <Typography.Text strong>v{v.version}</Typography.Text>
                        {isLatest && <Tag color="blue">最新</Tag>}
                        {v.aiGenerated ? <Tag color="purple">AI 生成</Tag> : <Tag>人工</Tag>}
                        {isPendingReview && <Tag color="orange">待評審</Tag>}
                        {v.submittedAt && !isPendingReview && <Tag color="green">已提交</Tag>}
                        {v.reviewedAt && (
                          <Tag color={v.reviewNotes ? 'red' : 'gold'}>
                            {v.reviewNotes ? '已退回' : '已核准'}
                          </Tag>
                        )}
                      </Space>
                    }
                    extra={
                      <Space>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDateTime(v.createdAt)}
                        </Typography.Text>
                        {canSubmitThisVersion && (
                          <Button
                            size="small"
                            type="primary"
                            icon={<Send size={12} />}
                            onClick={() => {
                              // 序44：定稿改走對話框——勾選驗證建議 + 簽名後送審
                              setFinalizeTarget(v);
                              setRecommendation(null);
                              setRecommendationNote('');
                            }}
                          >
                            {/* 序84：曾被初審退回時文案改「重新上報」 */}
                            {wasReturned ? '重新上報' : '定稿送審'}
                          </Button>
                        )}
                        {!isCaseLead &&
                          !v.submittedAt &&
                          report.status !== '終稿' &&
                          report.status !== '評審中' && (
                            <Tooltip title="校對完成後，由本案主導稽核員定稿送審">
                              <Tag color="default">待主導稽核員定稿</Tag>
                            </Tooltip>
                          )}
                        <Button
                          size="small"
                          type="text"
                          icon={<Eye size={14} />}
                          onClick={() => setViewingVersionId(v.id)}
                        >
                          查看
                        </Button>
                      </Space>
                    }
                  >
                    {/* 報告內容改由「查看」彈窗展示（見底部 Modal），此處只保留退回備註 */}
                    {v.reviewNotes && (
                      <Alert
                        style={{ marginTop: 8 }}
                        type="warning"
                        message="退回備註"
                        description={v.reviewNotes}
                        showIcon
                      />
                    )}
                  </Card>
                );
              })}
            </Space>
          )}
        </Card>
      )}

      {/* 查看報告內容 Modal（板塊化編輯器，草稿態可編輯；內容取即時資料，生成後自動刷新） */}
      <Modal
        title={
          viewingVersion ? (
            <Space size={8}>
              <FileText size={16} />
              <span>稽核報告內容</span>
              <Tag color="blue">v{viewingVersion.version}</Tag>
              {report?.status === '草稿' && <Tag color="gold">可編輯</Tag>}
            </Space>
          ) : (
            '稽核報告內容'
          )
        }
        open={viewingVersion !== null}
        onCancel={() => setViewingVersionId(null)}
        footer={null}
        width="80%"
        style={{ maxWidth: 1080, top: 32 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
        destroyOnHidden
      >
        {viewingVersion && (
          <AuditReportEditor
            content={viewingVersion.content}
            caseId={task.caseId}
            versionId={viewingVersion.id}
            scheme={task.caseScheme ?? undefined}
            editable={report?.status === '草稿'}
            onSaved={() => void invalidate()}
          />
        )}
      </Modal>

      {/* 退回 Modal */}
      <Modal
        title="退回報告"
        open={rejectTarget !== null}
        onCancel={() => {
          setRejectTarget(null);
          setRejectNotes('');
        }}
        onOk={() => {
          if (!rejectTarget) return;
          if (!rejectNotes.trim()) {
            void message.warning('請填寫退回原因');
            return;
          }
          rejectMutation.mutate({
            versionId: rejectTarget.id,
            reviewNotes: rejectNotes.trim(),
          });
        }}
        confirmLoading={rejectMutation.isPending}
        okText="確認退回"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text>
            將退回版本 <Tag color="blue">v{rejectTarget?.version}</Tag>
            ，請填寫退回原因（會通知提交人）：
          </Typography.Text>
          <Input.TextArea
            rows={4}
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="例如：第 3 章節缺少關鍵證據，請補充現場照片與引用條款……"
            maxLength={2000}
            showCount
          />
        </Space>
      </Modal>

      {/* 序44：定稿送審 Modal——主導稽核員勾選【驗證建議】+ 簽名後上報全部稽核文檔 */}
      <Modal
        title={wasReturned ? '重新上報（定稿送審）' : '定稿送審'}
        open={finalizeTarget !== null}
        onCancel={() => setFinalizeTarget(null)}
        onOk={() => {
          if (!finalizeTarget) return;
          if (!recommendation) {
            void message.warning('請勾選驗證建議');
            return;
          }
          if (recommendation !== 'issue' && !recommendationNote.trim()) {
            void message.warning('選擇部分範圍發放或不建議發放時，應說明原因');
            return;
          }
          submitMutation.mutate(finalizeTarget.id);
        }}
        confirmLoading={submitMutation.isPending}
        okText={wasReturned ? '重新上報' : '定稿送審'}
        cancelText="取消"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text>
            將定稿版本 <Tag color="blue">v{finalizeTarget?.version}</Tag>
            並上報全部稽核文檔給驗證決定審議小組；系統將同時通知其他稽核小組成員簽名。
          </Typography.Text>
          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>
              驗證建議（必選）
            </Typography.Text>
            <Select<'issue' | 'partial' | 'no_issue'>
              style={{ width: '100%', marginTop: 6 }}
              placeholder="請勾選驗證建議"
              value={recommendation ?? undefined}
              onChange={(v) => setRecommendation(v)}
              options={[
                { value: 'issue', label: '建議發放驗證證書' },
                { value: 'partial', label: '建議發放部分驗證範圍驗證證書（應說明原因）' },
                { value: 'no_issue', label: '不建議發放驗證證書（應說明原因）' },
              ]}
            />
          </div>
          <Input.TextArea
            rows={3}
            value={recommendationNote}
            onChange={(e) => setRecommendationNote(e.target.value)}
            placeholder={
              recommendation && recommendation !== 'issue' ? '說明原因（必填）' : '說明（選填）'
            }
            maxLength={2000}
            showCount
          />
          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>
              主導稽核員簽名
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              <Space>
                <Button
                  type="primary"
                  ghost
                  size="small"
                  loading={importSignatureMut.isPending}
                  onClick={() => importSignatureMut.mutate()}
                >
                  導入簽名檔
                </Button>
                {leadSignatureId && <Tag color="success">已簽名</Tag>}
              </Space>
            </div>
          </div>
        </Space>
      </Modal>
    </Space>
  );
}
